import { StepArgs } from '@/lib/Steppers';
import { createContext, FC, PropsWithChildren, useCallback, useMemo, useState } from 'react';
import { CREATE_CAMPAIGN_STEPS } from '../constants';
import { CreateCampaignStep } from '../types';

export type CampaignsValuesApi = {
  currentStep: StepArgs<CreateCampaignStep>;
  completedSteps: CreateCampaignStep[];
};

type CampaignsActionsApi = {
  setStep: (id: CreateCampaignStep) => void;
  setNextStep: VoidFunction;
};

export const CampaignsValuesContext = createContext<CampaignsValuesApi | null>(null);
export const CampaignsActionsContext = createContext<CampaignsActionsApi | null>(null);

type CampaignsProviderProps = PropsWithChildren & {
  initialStep?: number;
};

export const CampaignsProvider: FC<CampaignsProviderProps> = ({ initialStep = 0, children }) => {
  const [currentIndex, setCurrentIndex] = useState(initialStep);
  const [completedSteps, setCompletedSteps] = useState<CreateCampaignStep[]>([]);

  const setStep = useCallback(
    (id: CreateCampaignStep) => {
      setCurrentIndex(CREATE_CAMPAIGN_STEPS.findIndex((x) => x.id === id));
      setCompletedSteps((prev) => (prev.includes(id) ? prev : [...prev, id]));
    },
    [setCurrentIndex]
  );

  const setNextStep = useCallback(() => {
    setCurrentIndex((prev) => {
      const nextStepIndex = prev === CREATE_CAMPAIGN_STEPS.length - 1 ? 0 : prev + 1;
      const completedStepId = CREATE_CAMPAIGN_STEPS[prev].id;
      setCompletedSteps((prev) => (prev.includes(completedStepId) ? prev : [...prev, completedStepId]));
      return nextStepIndex;
    });
  }, [setCurrentIndex]);

  const value = useMemo(
    () =>
      ({
        currentStep: CREATE_CAMPAIGN_STEPS[currentIndex],
        completedSteps,
      }) satisfies CampaignsValuesApi,
    [currentIndex]
  );

  const actions = useMemo(
    () =>
      ({
        setStep,
        setNextStep,
      }) satisfies CampaignsActionsApi,
    [setStep]
  );

  return (
    <CampaignsValuesContext.Provider value={value}>
      <CampaignsActionsContext.Provider value={actions}>{children}</CampaignsActionsContext.Provider>
    </CampaignsValuesContext.Provider>
  );
};
